<template>
  <div id="main-wrapper">
    <NavbarDark />
    <div v-if="service" class="service-detail">
      <div class="banner">
        <img :src="service.image" alt="Service banner" />
      </div>
      <div class="card-wrapper">
        <div class="service-card">

          <h1 class="service-title">{{ service.name }}</h1>
          <div class="tags" v-if="service.tags?.length">
            <span v-for="tag in service.tags" :key="tag.id" class="tag">
              {{ tag.name }}
            </span>
          </div>
          <div
            class="service-description"
            v-html="service.long_description"
          ></div>
          <div class="faq" v-if="service.faqs?.length">
            <h3>{{$t('FAQs')}}</h3>

            <details
              v-for="faq in service.faqs"
              :key="faq.id"
              class="faq-item"
            >
              <summary>{{ faq.question }}</summary>
              <p>{{ faq.answer }}</p>
            </details>
          </div>

        </div>
      </div>
    </div>

    <div v-else class="loading">
      {{ $t('Loading service...') }}
    </div>

    <FooterTop :bg="'theme-bg'" />
    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import NavbarDark from '@/components/navbar/navbar-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'
import { computed } from 'vue';
import { useHead } from '@vueuse/head'

const service = ref(null)
const route = useRoute()
const { locale } = useI18n()
const router = useRouter();

const fetchService = async () => {
  try {
    const { data } = await axios.get(
      `/services/${route.params.serviceSlug}?lang=${locale.value}`
    )
    service.value = data
  } catch (err) {
    console.error('Error fetching service:', err)
  }
}

///// SEO && Google
const baseUrl = 'https://ruqm.me'

const canonicalUrl = computed(() => {
  if (!service.value?.slug?.[locale.value]) return ''
  return `${baseUrl}/${locale.value}/service/${service.value.slug[locale.value]}`
})

const hreflangLinks = computed(() => {
  if (!service.value?.slug) return []

  return Object.entries(service.value.slug).map(([lang, slug]) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}/service/${slug}`
  }))
})

const xDefault = computed(() => {
  const enSlug = service.value?.slug?.en
  if (!enSlug) return null

  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/en/service/${enSlug}`
  }
})

useHead(() => {
  if (!service.value?.id) return {}

  return {
    title: service.value.seo_title || service.value.name,
    meta: [
      {
        name: 'description',
        content: service.value.seo_description || ''
      },
      {
        name: 'keywords',
        content: service.value.focus_keywords?.join(', ') || ''
      },

      { property: 'og:type', content: 'event' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:title', content: service.value.seo_title },
      { property: 'og:description', content: service.value.seo_description },
      { property: 'og:image', content: service.value.seo_image },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: service.value.seo_title },
      { name: 'twitter:description', content: service.value.seo_description },
      { name: 'twitter:image', content: service.value.seo_image },
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
      ...(xDefault.value ? [xDefault.value] : [])
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": service.value.name || 'Service',
            "description": service.value.seo_description,
            "image": service.value.seo_image || [],
            "url": `https://ruqm.me${route.path}`,
        }),
        key: "schema"
      }
    ]
  }
})

watch(locale, async (newLocale) => {

    if (!service.value?.slug) return;

    const localizedSlug = service.value.slug[newLocale];
    if (!localizedSlug) {
        await router.push({ name: 'NotFound' });
        return;
    }
    const newPath = `/${newLocale}/service/${localizedSlug}`;

    if (route.fullPath !== newPath) {
        await router.replace(newPath);
    }
    await fetchService()
});
onMounted(fetchService)

</script>

<style scoped>
.service-detail {
  background: #f4f6f8;
  min-height: 100vh;
}

/* Banner */
.banner {
  height: 320px;
  overflow: hidden;
}

.banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Card */
.card-wrapper {
  display: flex;
  justify-content: center;
  margin-top: -90px;
  padding: 0 16px;
}

.service-card {
  background: white;
  max-width: 900px;
  width: 100%;
  padding: 36px;
  border-radius: 14px;
  box-shadow: 0 14px 35px rgba(0, 0, 0, 0.12);
}

/* Title */
.service-title {
  font-size: 30px;
  margin-bottom: 12px;
}

/* Tags */
.tags {
  margin-bottom: 20px;
}

.tag {
  display: inline-block;
  background: #eef2ff;
  color: #4338ca;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  margin-right: 8px;
}

/* Description */
.service-description {
  font-size: 16px;
  line-height: 1.8;
  color: #444;
}

.service-description img {
  max-width: 100%;
  border-radius: 10px;
  margin: 20px 0;
}

/* FAQ */
.faq {
  margin-top: 40px;
}

.faq h3 {
  margin-bottom: 16px;
}

.faq-item {
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #f9fafb;
}

.faq-item summary {
  cursor: pointer;
  font-weight: 600;
}

.loading {
  padding: 120px;
  text-align: center;
  font-size: 18px;
}
</style>
